package com.arenaproplay.admin.pages.analytics

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.AdminRepository
import com.arenaproplay.admin.domain.admin.PaywallFunnelDto
import com.arenaproplay.admin.domain.admin.PaywallSourceRow
import com.arenaproplay.admin.domain.admin.Period
import com.arenaproplay.admin.domain.admin.PlatformFilter
import com.arenaproplay.admin.ui.components.admin.DataTable
import com.arenaproplay.admin.ui.components.admin.GlassCard
import com.arenaproplay.admin.ui.components.admin.TableColumn
import com.arenaproplay.admin.ui.components.admin.VSpace
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.theme.displaySmall
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import org.jetbrains.compose.web.css.*

// ── Paywall funnel tab ────────────────────────────────────────────────────────

@Composable
fun PaywallTabContent(period: Period, repo: AdminRepository) {
    // Restore platform from URL on first composition
    var platform by remember {
        mutableStateOf(
            PlatformFilter.entries.find { it.code == urlParam("platform") }
                ?: PlatformFilter.ALL
        )
    }
    var data by remember { mutableStateOf<PaywallFunnelDto?>(null) }
    var error by remember { mutableStateOf<String?>(null) }

    // Fetch funnel data; also sync ?platform= into the URL
    LaunchedEffect(period, platform) {
        setUrlParams(
            "platform" to platform.param,
        )
        data = null
        error = null
        runCatching { repo.getPaywallFunnel(period, platform.param) }
            .onSuccess { data = it }
            .onFailure { error = it.message ?: "Ошибка загрузки" }
    }

    Column(Modifier.fillMaxWidth()) {

        // ── Platform filter chips (same visual pattern as RevStatusChip) ──────
        Row(
            Modifier.styleModifier {
                property("display", "flex")
                property("gap", "4px")
                property("flex-wrap", "wrap")
            }
        ) {
            PlatformFilter.entries.forEach { pf ->
                PaywallPlatformChip(
                    label = pf.label,
                    active = platform == pf,
                    onClick = { platform = pf },
                )
            }
        }

        VSpace(14)

        // ── Funnel bars ───────────────────────────────────────────────────────
        GlassCard(padding = 16) {
            SpanText("Воронка пейвола", Modifier.displaySmall().color(AppColors.Ink))
            Box(Modifier.height(12.px))
            when {
                error != null -> SpanText(
                    "Ошибка: $error",
                    Modifier.bodySRegular().color(AppColors.Error)
                )
                data == null -> {
                    // Loading skeleton — NO text per house style
                    Column(Modifier.fillMaxWidth().styleModifier { property("gap", "6px") }) {
                        val widths = listOf("100%", "55%", "24%")
                        widths.forEachIndexed { i, w ->
                            Box(
                                Modifier.height(32.px).sBorderRadius(8)
                                    .styleModifier {
                                        property("width", w)
                                        property("background", "rgba(255,255,255,.04)")
                                    }
                            )
                            if (i < widths.lastIndex) Box(Modifier.height(6.px))
                        }
                    }
                }
                else -> {
                    val steps = data!!.steps
                    val base = (steps.firstOrNull()?.userCount ?: 1L).coerceAtLeast(1L)
                    val gradients = listOf(
                        "linear-gradient(90deg,#0061FF,#4D8CFF)",
                        "linear-gradient(90deg,#12B76A,#31D583)",
                        "linear-gradient(90deg,#F5A524,#FFC65C)",
                    )
                    Column(Modifier.fillMaxWidth().styleModifier { property("gap", "6px") }) {
                        steps.forEachIndexed { i, step ->
                            NaFunnelBar(
                                label = step.label,
                                count = step.userCount,
                                widthPct = step.userCount.toDouble() / base * 100.0,
                                background = gradients[i.coerceAtMost(gradients.lastIndex)],
                                ratioLabel = if (i == 0) null
                                    else "${step.userCount * 100 / base}%",
                                bold = i == steps.lastIndex,
                            )
                        }
                    }
                }
            }
        }

        VSpace(14)

        // ── Per-source table ──────────────────────────────────────────────────
        GlassCard(padding = 0) {
            Box(
                Modifier.fillMaxWidth().styleModifier {
                    property("padding", "12px 16px 10px 16px")
                    property("border-bottom", "1px solid rgba(255,255,255,.06)")
                }
            ) {
                SpanText("По источникам", Modifier.displaySmall().color(AppColors.Ink))
            }

            val rows: List<PaywallSourceRow> = data?.sources ?: emptyList()
            DataTable(
                items = rows,
                loading = data == null && error == null,
                emptyText = "Нет данных",
                defaultSortIndex = 3,   // «Оплатил» column, descending
                defaultSortAsc = false,
                columns = listOf(
                    TableColumn(
                        header = "Источник",
                        weight = 1.5f,
                        sortKey = { it.source },
                    ) { row ->
                        SpanText(row.source, Modifier.bodySRegular().color(AppColors.Ink))
                    },
                    TableColumn(
                        header = "Показан",
                        sortKey = { it.shown },
                    ) { row ->
                        SpanText(row.shown.toString(), Modifier.bodySRegular().color(AppColors.Muted))
                    },
                    TableColumn(
                        header = "Нажал купить",
                        sortKey = { it.tapped },
                    ) { row ->
                        SpanText(row.tapped.toString(), Modifier.bodySRegular().color(AppColors.Muted))
                    },
                    TableColumn(
                        header = "Оплатил",
                        sortKey = { it.purchased },
                    ) { row ->
                        SpanText(row.purchased.toString(), Modifier.bodySRegular().color(AppColors.Ink))
                    },
                    TableColumn(
                        header = "Конверсия",
                        sortKey = { if (it.shown > 0) it.purchased.toDouble() / it.shown else 0.0 },
                    ) { row ->
                        val conv = if (row.shown > 0) row.purchased * 100.0 / row.shown else 0.0
                        // Round to one decimal: multiply by 10, truncate, divide by 10
                        val convStr = "${(conv * 10).toInt() / 10.0}%"
                        SpanText(convStr, Modifier.bodySRegular().color(AppColors.Ink))
                    },
                ),
            )
        }
    }
}

// ── Platform chip ─────────────────────────────────────────────────────────────

@Composable
private fun PaywallPlatformChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(999)
            .classNames("na-press")
            .styleModifier {
                property("padding", "4px 12px")
                property("cursor", "pointer")
                property("user-select", "none")
                property("transition", "all 140ms ease")
                property("font-family", "'Inter', sans-serif")
                property("font-size", "11px")
                if (active) {
                    property("background", "rgba(0,97,255,.30)")
                    property("border", "1px solid rgba(77,140,255,.40)")
                    property("color", "#8FB8FF")
                    property("font-weight", "600")
                } else {
                    property("background", "rgba(255,255,255,.04)")
                    property("border", "1px solid rgba(255,255,255,.08)")
                    property("color", "rgba(240,244,252,.55)")
                    property("font-weight", "400")
                }
            }
            .onClick { onClick() }
    ) {
        SpanText(label, Modifier)
    }
}
